import { useState } from "react"
import { Trans } from "react-i18next"
import styled from "styled-components"

import { Banner, PinInput, spacing, Text } from "@minuthq/meatball-ui-react"

import { useGetUser } from "src/data/user/hooks/useGetUser"
import {
  ERROR_CODE,
  usePostRequestUserVerificationCode,
  usePostVerifyEmailCode,
  VALID_CODE,
} from "src/data/user/queries/userQueries"
import { useTimer } from "src/hooks/useTimer"
import { useTranslate } from "src/i18n/useTranslate"
import { MButton } from "src/ui/Button/MButton"
import { MText } from "src/ui/MText"

export function StepVerifyEmail({ onVerified }: { onVerified: () => void }) {
  const { t, langKeys } = useTranslate()
  const user = useGetUser()

  const [code, setCode] = useState("")

  const { timeLeft, startTimer, isTimerRunning } = useTimer()

  const postRequestUserVerificationCode = usePostRequestUserVerificationCode()
  const postVerifyEmailCode = usePostVerifyEmailCode()

  function handleVerifyCode() {
    postVerifyEmailCode.mutate(
      {
        _userId: user.user_id,
        code,
      },
      {
        onSuccess: () => {
          onVerified()
        },
        onError: () => {
          setCode("")
        },
      }
    )
  }

  function handleClickResendCode() {
    postRequestUserVerificationCode.mutate(
      {
        path: { user_id: user.user_id },
        // TODO: GT-2004 Set verification type to code in body
        // body: {},
      },
      {
        onSuccess: () => {
          startTimer(60) // 60 seconds cooldown timer
        },
      }
    )
  }

  const codeIsInvalid =
    postVerifyEmailCode.isError && postVerifyEmailCode.error === "Invalid code"

  return (
    <Wrapper>
      <Text variant="heading2" marginBottom={spacing.L}>
        {t(langKeys.email_code_verification_title)}
      </Text>
      <Text marginBottom={spacing.M}>
        <Trans
          i18nKey={t(langKeys.email_code_verification_description, {
            email: user.email,
          })}
          components={{
            bold: (
              <MText variant="subtitle" as="span">
                {user.email}
              </MText>
            ),
          }}
        />
      </Text>

      {/* TODO: GT-2004 Remove this once BE is implemented */}
      <Text variant="subtitle">Test codes:</Text>
      <Text>Valid code: {VALID_CODE}</Text>
      <Text>Error code: {ERROR_CODE}</Text>
      <Text>Any other code is invalid</Text>

      <PinWrapper>
        <PinInput
          value={code}
          onChange={(value) => {
            setCode(value)
          }}
          onComplete={handleVerifyCode}
          error={codeIsInvalid}
          loading={postVerifyEmailCode.isLoading}
        />
        {codeIsInvalid && (
          <div>
            <Text variant="bodyS" color="emergency">
              {t(langKeys.email_code_verification_invalid_code)}
            </Text>
            <Text variant="bodyS" color="emergency">
              {t(langKeys.email_code_verification_invalid_code_suggestions)}
            </Text>
          </div>
        )}
      </PinWrapper>

      <Text variant="subtitle" marginBottom={spacing.XS2}>
        {t(langKeys.email_code_verification_no_email_title)}
      </Text>
      <BottomWrapper>
        <Text color="secondary">
          {t(langKeys.email_code_verification_no_email_description)}
        </Text>

        {(postRequestUserVerificationCode.isError ||
          (postVerifyEmailCode.isError &&
            postVerifyEmailCode.error !== "Invalid code")) && (
          <Banner type="error" fullWidth>
            {t(langKeys.failed_something_went_wrong)}
          </Banner>
        )}

        <MButton
          variant="minimal"
          onClick={handleClickResendCode}
          disabled={isTimerRunning}
          loading={postRequestUserVerificationCode.isLoading}
        >
          {t(langKeys.email_code_verification_resend)}{" "}
          {isTimerRunning && `(${timeLeft})`}
        </MButton>
      </BottomWrapper>
    </Wrapper>
  )
}

const PinWrapper = styled.div`
  margin-bottom: ${spacing.XL2};
`

const Wrapper = styled.div`
  margin-top: ${spacing.L};
`

const BottomWrapper = styled.div`
  display: grid;
  justify-items: start;
  gap: ${spacing.XL};
`
